
import React, { useEffect, useRef, useState } from 'react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useAdminTheme } from '../contexts/AdminThemeContext';

const AdminNotificationListener: React.FC = () => {
    const { currentUser } = useAuth();
    const { showNotification, showSuccess } = useNotification();
    const { isDark } = useAdminTheme();
    const [soundEnabled, setSoundEnabled] = useState(true);

    useEffect(() => {
        const loadSetting = () => {
            const saved = localStorage.getItem('admin_enableSignupSound');
            if (saved !== null) {
                setSoundEnabled(saved === 'true');
            }
        };
        loadSetting();

        const handleUpdate = () => loadSetting();
        window.addEventListener('notificationSettingsUpdated', handleUpdate);
        return () => window.removeEventListener('notificationSettingsUpdated', handleUpdate);
    }, []);

    // Audio context ref to reuse it
    const audioContextRef = useRef<AudioContext | null>(null);

    const playNotificationSound = () => {
        if (!soundEnabled) return;

        try {
            const audio = new Audio('/sounds/new%20user.wav');
            audio.play().catch(error => {
                console.error('Error playing notification sound:', error);
            });
        } catch (error) {
            console.error('Error initializing audio:', error);
        }
    };

    useEffect(() => {
        if (!currentUser) return;

        // Listen for new users
        // Order by createdAt desc to get the newest one
        const q = query(
            collection(db, 'users'),
            orderBy('createdAt', 'desc'),
            limit(1)
        );

        let isInitialSnapshot = true;

        const unsubscribe = onSnapshot(q, (snapshot) => {
            // Skip the very first snapshot (initial load of this listener)
            if (isInitialSnapshot) {
                isInitialSnapshot = false;
                return;
            }

            // Check for changes
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const userData = change.doc.data();
                    console.log('New user signup detected:', userData.email);

                    // Play sound
                    playNotificationSound();

                    // Show visual notification
                    showSuccess(`New user registered: ${userData.email || 'Unknown'}`);
                }
            });
        }, (error) => {
            console.error('Error listening for new users:', error);
        });

        return () => unsubscribe();
    }, [currentUser, showSuccess, soundEnabled]); // Added soundEnabled dependency to be safe (though ref is better if we don't want re-sub), but local var handles re-sub safely now.

    return null; // This component doesn't render anything visible
};

export default AdminNotificationListener;
